import { base64Encode } from "@openanalyst-ai/core/util/encode"
import { type IconName, isIconName } from "@openanalyst-ai/ui/icon"
import { findBrand, type BrandIcon } from "@/plugins/brand-icons"
import {
  canonicalPluginAppID,
  pluginAppSessionHref,
  pluginLinks,
  type RuntimePluginApp,
} from "@/plugins/runtime"

// Intent buckets: each plugin is classified by matching tokens from
// id + title + capabilities against these keyword lists. The best-scoring
// bucket dictates which icon is chosen. If the primary icon is taken,
// alt icons within the bucket are tried; if those are taken too, the
// next-best bucket is consulted; final fallback is `plugin`.
type IntentBucket = {
  name: string
  keywords: string[]
  primary: IconName
  alts: IconName[]
}

const INTENT_BUCKETS: IntentBucket[] = [
  {
    name: "store",
    keywords: ["shopify", "store", "storefront", "shop", "ecommerce", "orders", "products", "cart", "inventory", "merch"],
    primary: "shopify",
    alts: ["storefront"],
  },
  {
    name: "meta-ads",
    keywords: ["meta", "facebook", "fb", "instagram", "ig"],
    primary: "meta-ads",
    alts: ["ads"],
  },
  {
    name: "ads",
    keywords: ["ads", "advertising", "campaign", "campaigns", "paid", "marketing", "promotion"],
    primary: "ads",
    alts: ["meta-ads"],
  },
  {
    name: "analytics",
    keywords: ["analytics", "reporting", "metrics", "insights", "attribution", "dashboard", "stats", "kpi"],
    primary: "analytics",
    alts: ["dashboard"],
  },
  {
    name: "crm",
    keywords: ["crm", "customer", "customers", "lead", "leads", "contact", "contacts", "sales", "pipeline", "deal", "deals"],
    primary: "crm",
    alts: ["users"],
  },
  {
    name: "team",
    keywords: ["team", "member", "members", "people", "user", "users", "staff", "employee"],
    primary: "users",
    alts: ["crm"],
  },
  {
    name: "messaging",
    keywords: ["email", "mail", "inbox", "chat", "slack", "message", "messaging", "whatsapp", "gmail", "outlook"],
    primary: "mail",
    alts: ["speech-bubble", "bubble-5"],
  },
  {
    name: "design-studio",
    keywords: ["palette"],
    primary: "palette",
    alts: ["pencil-line"],
  },
  {
    name: "creative",
    keywords: ["design", "creative", "brand", "image", "video", "lipsync", "cinema", "render", "art", "visual"],
    primary: "sparkles",
    alts: ["generative-studio"],
  },
  {
    name: "ai",
    keywords: ["ai", "memory", "brain", "knowledge", "learn", "embedding", "vector", "10x-memory", "10xmemory"],
    primary: "brain",
    alts: ["sparkles"],
  },
  {
    name: "content",
    keywords: ["content", "document", "documents", "blog", "publish", "publishing", "write", "writing", "article", "post", "posts"],
    primary: "document",
    alts: ["receipt-text"],
  },
  {
    name: "finance",
    keywords: ["billing", "invoice", "payment", "payments", "finance", "receipt", "subscription", "stripe", "revenue"],
    primary: "receipt-text",
    alts: ["document"],
  },
  {
    name: "seo",
    keywords: ["seo", "search", "keyword", "keywords", "ranking", "serp", "backlink"],
    primary: "magnifying-glass",
    alts: ["analytics"],
  },
  {
    name: "code",
    keywords: ["code", "dev", "developer", "programming", "git", "github", "repo", "repository"],
    primary: "code-lines",
    alts: ["window-cursor"],
  },
  {
    name: "workspace",
    keywords: ["workspace", "panels", "panel", "layout", "workspace-app"],
    primary: "panels-top-left",
    alts: ["dashboard"],
  },
  {
    name: "files",
    keywords: ["file", "files", "folder", "tree", "navigation", "drive", "dropbox"],
    primary: "file-tree",
    alts: ["folder-add-left"],
  },
  {
    name: "activity",
    keywords: ["activity", "monitor", "monitoring", "status", "observability", "log", "logs", "lsp"],
    primary: "activity",
    alts: ["analytics"],
  },
]

// Fallback pool used only when intent matching exhausts all bucket
// options (extremely rare). These are generic, semantically-neutral icons.
const FALLBACK_ICON_POOL: IconName[] = [
  "plugin",
  "briefcase-business",
  "panels-top-left",
  "dashboard",
  "speech-bubble",
  "sparkles",
]

function normalizeToken(value?: string): string {
  return (value ?? "").trim().toLowerCase().replace(/[^a-z0-9]+/g, " ").trim()
}

function pluginTokens(item: { id: string; icon: string; title: string; capabilities?: string[] }): string {
  // Only id/icon/title and SEMANTIC capabilities drive icon-bucket matching.
  // Internal, namespaced plumbing capabilities (e.g. "plugin:file-defined",
  // "socket:plugin-app", "ui:dynamic-dashboard") are NOT intent signals — and
  // "plugin:file-defined" in particular leaked a "file" token that dragged every
  // file-defined plugin into the "files" bucket (Understand → folder-add-left).
  const semanticCaps = (item.capabilities ?? []).filter((cap) => !cap.includes(":"))
  return [item.id, item.icon, item.title, ...semanticCaps].map(normalizeToken).filter(Boolean).join(" ")
}

function scoreBucket(tokens: string, bucket: IntentBucket): number {
  const haystack = ` ${tokens} `
  let score = 0
  for (const keyword of bucket.keywords) {
    // Match keyword as a whole token (avoid partial matches like "ads" in "leads").
    if (haystack.includes(` ${keyword} `) || haystack.includes(`-${keyword}-`) || haystack.endsWith(` ${keyword}`)) {
      score += 1
    }
  }
  return score
}

function rankedBucketsFor(item: { id: string; icon: string; title: string; capabilities?: string[] }): IntentBucket[] {
  const tokens = pluginTokens(item)
  return INTENT_BUCKETS.map((bucket) => ({ bucket, score: scoreBucket(tokens, bucket) }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((entry) => entry.bucket)
}

// Distinct accent colors for plugin rail icons. Each plugin gets a UNIQUE
// color — no two plugins in the project share an accent.
const PLUGIN_COLOR_PALETTE = [
  "#0668e1", // meta blue
  "#95bf47", // shopify green
  "#ff7a59", // hubspot orange
  "#635bff", // stripe purple
  "#e37400", // analytics amber
  "#0891b2", // teal
  "#ea4335", // gmail red
  "#8b5cf6", // violet
  "#0f9d58", // drive green
  "#4285f4", // docs blue
  "#1877f2", // facebook blue
  "#a0522d", // sienna
  "#dc2626", // red
  "#059669", // emerald
  "#7c3aed", // purple
  "#db2777", // pink
  "#ca8a04", // yellow-amber
  "#0d9488", // teal-dark
  "#d97706", // amber-orange
  "#65a30d", // lime
  "#475569", // slate
  "#0284c7", // sky blue
]

export type ResolvedPluginIcon = { kind: "brand"; brand: BrandIcon } | { kind: "icon"; name: IconName }

export function resolveUniquePluginIcons(
  items: { id: string; icon: string; title: string; capabilities?: string[] }[],
): ResolvedPluginIcon[] {
  const usedBrands = new Set<string>()
  const usedIcons = new Set<IconName>()
  const assigned: ResolvedPluginIcon[] = []
  for (const item of items) {
    // 1. BRAND match first — if the plugin clearly maps to a known
    //    brand (Shopify, Meta, Stripe, …), use the actual brand glyph.
    //    No two plugins ever share a brand glyph.
    const brand = findBrand(item)
    if (brand && !usedBrands.has(brand.id)) {
      usedBrands.add(brand.id)
      assigned.push({ kind: "brand", brand })
      continue
    }
    // 2. Intent bucket match — best-scoring bucket wins; primary first,
    //    then alts, then walk to the next bucket.
    let picked: IconName | undefined
    for (const bucket of rankedBucketsFor(item)) {
      const candidates: IconName[] = [bucket.primary, ...bucket.alts]
      picked = candidates.find((candidate) => !usedIcons.has(candidate))
      if (picked) break
    }
    // 3. Honor the plugin's preferred icon if it's a valid IconName and free.
    if (!picked) {
      const preferred = (isIconName(item.icon) ? item.icon : undefined) as IconName | undefined
      if (preferred && !usedIcons.has(preferred)) picked = preferred
    }
    // 4. Last resort: generic fallback.
    if (!picked) {
      picked = FALLBACK_ICON_POOL.find((candidate) => !usedIcons.has(candidate)) ?? "plugin"
    }
    usedIcons.add(picked)
    assigned.push({ kind: "icon", name: picked })
  }
  return assigned
}

export function pluginColorForIndex(index: number): string {
  return PLUGIN_COLOR_PALETTE[index % PLUGIN_COLOR_PALETTE.length]!
}

export function pluginEnabledOverrideKey(directory: string, id: string) {
  return `${directory}\n${canonicalPluginAppID(id)}`
}

export function pluginEnabledByOverride(input: { directory: string; id: string; overrides: Record<string, boolean> }) {
  return input.overrides[pluginEnabledOverrideKey(input.directory, input.id)] !== false
}

export function projectPluginLinks(input: { directory: string; apps: RuntimePluginApp[] }) {
  const dir = base64Encode(input.directory)
  return pluginLinks({ dir, apps: input.apps }).map((item) => {
    const inspiration = canonicalPluginAppID(item.id) === "marketing-calendar"
    const search = new URLSearchParams({ pluginExpanded: "1" })
    if (inspiration) search.set("pluginView", "inspiration")
    return {
      ...item,
      ...(inspiration ? { title: "Inspiration", icon: "sparkles" } : {}),
      href: pluginAppSessionHref({ dir, id: item.id, search }),
    }
  })
}
